import { useQuery } from "react-query";

import { useLicenses } from "~/entity/Licenses/hooks/useLicenses";
import { fetchAllLicenseOrganizations } from "~/entity/Licenses/lib/fetchAllLicenseOrganizations";
import { ILicenseOrganizationRowApi } from "~/entity/Licenses/model/schema";
import { PartnersService } from "~/entity/Partners/api/service";

import { ILicenseOwner, ILicenseSelectOption } from "./types";

const PAGE_SIZE = 100;

export const parseOwnerValue = (value: string): ILicenseOwner => {
  const separatorIndex = value.indexOf(":");

  if (separatorIndex === -1) {
    return {};
  }

  const kind = value.slice(0, separatorIndex);
  const id = value.slice(separatorIndex + 1);

  if (kind === "org") {
    return { organizationId: id };
  }

  return { dealerId: Number(id) };
};

const toOwnerOption = (
  row: ILicenseOrganizationRowApi,
): ILicenseSelectOption | null => {
  if (row.ownerType === "PARTNER" && row.organizationId) {
    return {
      value: `org:${row.organizationId}`,
      label: row.name || row.organizationId,
    };
  }

  if (row.dealerId != null) {
    return {
      value: `dealer:${row.dealerId}`,
      label: row.name || String(row.dealerId),
    };
  }

  return null;
};

const mapOwnerOptions = (rows: ILicenseOrganizationRowApi[]) => {
  const options: ILicenseSelectOption[] = [];

  rows.forEach((row) => {
    const option = toOwnerOption(row);

    if (option) {
      options.push(option);
    }
  });

  return options;
};

const fetchAllActivePartnerOptions = async (): Promise<
  ILicenseSelectOption[]
> => {
  const options: ILicenseSelectOption[] = [];
  let page = 0;

  for (;;) {
    const response = await PartnersService.GET.list({
      page,
      count: PAGE_SIZE,
      status: "ACTIVE",
    });
    const partners = response.data.partners || [];

    partners.forEach((partner) => {
      options.push({ value: `org:${partner.id}`, label: partner.name });
    });

    if (partners.length < PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return options;
};

const fetchOwnerOptions = async (includeAllPartners: boolean) => {
  const [rows, partnerOptions] = await Promise.all([
    fetchAllLicenseOrganizations(),
    includeAllPartners
      ? fetchAllActivePartnerOptions().catch((error) => {
          console.error("Ошибка загрузки партнёров:", error);
          return [];
        })
      : Promise.resolve([]),
  ]);
  const options = mapOwnerOptions(rows);
  const seen: Record<string, boolean> = {};

  options.forEach((option) => {
    seen[option.value] = true;
  });

  partnerOptions.forEach((option) => {
    if (!seen[option.value]) {
      options.push(option);
      seen[option.value] = true;
    }
  });

  return options;
};

export const useLicenseOwnerOptions = (includeAllPartners = false) => {
  const { queryKeys } = useLicenses();
  const { data, isLoading } = useQuery(
    [
      ...queryKeys.organizations(undefined),
      includeAllPartners ? "withActivePartners" : "licenseOwners",
    ],
    () => fetchOwnerOptions(includeAllPartners),
  );

  return { options: data ?? [], loading: isLoading };
};
